import { appConfig } from '../../helpers/app-config/app-config';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import type { FloraPage, FloraPageViewUrl, FloraPageWritePayload } from './flora-pages-client.type';

const axiosInstance = AxiosInstance();
const baseURL = `${appConfig.VITE_APP_API_URL}/api/flora-pages/`;

/**
 * Lists the Flora pages visible to the current user in an organization
 * (their own pages plus org-shared ones), newest first.
 *
 * @param {string} organizationId - The organization UUID; required by the BE
 * @returns {Promise<FloraPage[]>} Pages in reverse-chronological order
 */
function getFloraPages(organizationId: string): Promise<FloraPage[]> {
  return axiosInstance
    .get<FloraPage[]>(baseURL, { params: { organization_id: organizationId } })
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch pages';
      console.error('Error fetching flora pages:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Retrieves a single Flora page's metadata by id.
 *
 * @param {string} id - The page UUID
 * @returns {Promise<FloraPage>} The page record
 */
function getFloraPage(id: string): Promise<FloraPage> {
  return axiosInstance
    .get<FloraPage>(`${baseURL}${id}/`)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch page';
      console.error('Error fetching flora page:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Updates a page's title and/or visibility. Owner-only on the BE.
 *
 * @param {string} id - The page UUID
 * @param {FloraPageWritePayload} payload - Fields to change
 * @returns {Promise<FloraPage>} The updated page record
 */
function updateFloraPage(id: string, payload: FloraPageWritePayload): Promise<FloraPage> {
  return axiosInstance
    .patch<FloraPage>(`${baseURL}${id}/`, payload)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update page';
      console.error('Error updating flora page:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Deletes a page and its stored HTML. Owner-only on the BE.
 *
 * @param {string} id - The page UUID
 * @returns {Promise<void>} Resolves on success
 */
function deleteFloraPage(id: string): Promise<void> {
  return axiosInstance
    .delete(`${baseURL}${id}/`)
    .then(() => undefined)
    .catch((error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete page';
      console.error('Error deleting flora page:', errorMessage);
      throw new Error(errorMessage);
    });
}

/**
 * Mints a short-lived signed URL for loading the page's HTML into the
 * sandboxed viewer iframe. The token expires quickly — fetch fresh on each
 * viewer mount and never cache the result.
 *
 * @param {string} id - The page UUID
 * @returns {Promise<FloraPageViewUrl>} The signed URL and its TTL in seconds
 */
function getFloraPageViewUrl(id: string): Promise<FloraPageViewUrl> {
  return axiosInstance
    .get<FloraPageViewUrl>(`${baseURL}${id}/view-url/`)
    .then((response) => response.data)
    .catch((error) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch page view URL';
      console.error('Error fetching flora page view URL:', errorMessage);
      throw new Error(errorMessage);
    });
}

export { deleteFloraPage, getFloraPage, getFloraPages, getFloraPageViewUrl, updateFloraPage };
